import type { FlowReport, FlowReportStatus } from '../../../app-core/lib/flow/FlowReport'
import { IDB_flowReports, IDB_posts, IDB_procedures } from '../IDB/IDB.dexie'
import type { Post_TPostId } from '../Post/Post.TPost'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import { Serv_clientWithLogger } from '../Serv/Serv.client'
import { User_get } from '../User/User.get'
import { WF_ensureVersionOnDisk } from '../WF/WF.WorkflowReference'
import type { WorkflowReference } from '../../../shared/domain/workflows/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { Flow_dispatchOpenStateChange } from './Flow.updateSubscriber'
import { compact, difference } from 'lodash'
import { dictValues } from '../../../shared/lib/functional/dictionary.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { flatten, uniq } from '../../../shared/lib/functional/array.no-deps'
import { DS_getData } from '../DS/DS.Data'
import { Unit_writeUnits } from '../Unit/Unit.sync'
import * as LaunchDarkly from '../Util/Util.launchdarkly'

export async function Flow_downloadCollaborativeReports(logger: ClientLogger) {
    // Get the ids of the reports that the server thinks we should be working on
    const { ongoingReports: collaborativeReportIds } = await Serv_clientWithLogger(logger).getCollaborativeReports()

    const parallelRequests = LaunchDarkly.getFlagValue('parallel-ongoing-reports', false)

    if (parallelRequests) {
        const reportPromises = []

        // Sync each one, one at a time
        for (const reportId of collaborativeReportIds) {
            reportPromises.push(downloadCollaborativeReport(logger, reportId).catch(logger.error))
        }

        await Promise.all(reportPromises)
        return
    }

    for (const reportId of collaborativeReportIds) {
        await downloadCollaborativeReport(logger, reportId).catch(logger.error)
    }
}

async function downloadCollaborativeReport(logger: ClientLogger, reportClientId: ReportClientId) {
    // Get the flow report
    let flowReport = await IDB_flowReports().get(reportClientId)

    // If report exists, bail
    if (flowReport != null) return

    // Download the report
    flowReport = await downloadOngoingReport(logger, reportClientId)

    // Make sure we have the workflow for the report
    await WF_ensureVersionOnDisk(logger, flowReport.workflow)
}

export async function downloadOngoingReport(
    logger: ClientLogger,
    reportId: ReportClientId,
    status: FlowReportStatus = 'open'
): Promise<FlowReport<WorkflowReference>> {
    const { report, scheduledInstance } = await Serv_clientWithLogger(logger).getOngoingReport(reportId)
    const procedure = await IDB_procedures().get(report.procedure)

    // If procedure is undefined or does not exist in indexedDb then create an unknown procedure
    const reportProcedure = procedure ?? {
        id: report.procedure,
        name: 'Unknown procedure',
        isDeleted: true,
        isAssigned: true,
        description: '',
    }

    const flowReport: FlowReport<WorkflowReference> = {
        id: report.clientId,
        serverId: report.id,
        workflow: report.workflow,
        scheduledInstance,
        startDate: report.startDate,
        createdBy: report.createdBy,
        teamDoneFor: report.teamDoneFor,
        origin: report.origin,
        isAction: report.isAction,
        flowUnitIds: report.flowUnitIds,
        loopPlan: report.loopPlan,
        cachedUnitData: report.cachedUnitData,
        reportStatusId: report.reportStatusId,
        subjectUnitId: report.subjectUnitId,
        subjectUnitData: report.subjectUnitData,
        subjectUnitVersion: report.subjectUnitVersion,
        parentClientId: report.parentClientId,
        progenitorClientId: report.progenitorClientId,
        parentSubjectUnitId: report.parentSubjectUnitId,
        coordinates: report.coordinates,
        status,
        procedure: reportProcedure,
        title: null,
        uploadState: 'uploaded',
        collaborators: { invited: [], accepted: [report.createdBy, User_get().id] },
        lastSyncTime: new Date(0),
        postId: report.post,
    }

    await IDB_flowReports().put(flowReport)

    // Construct a post from other data source
    await IDB_posts().put({
        id: report.post as Post_TPostId,
        unit: report.subjectUnitId ?? null,
        location: report.location,
        workflow: report.workflow.id,
        procedure: report.procedure,
        createdAt: report.startDate,
        createdBy: report.createdBy,
        lastModifiedAt: report.startDate,
        lastModifiedBy: report.createdBy,
        lastModifiedType: 'created',
        serverSyncTime: report.submittedDate,
        isDeleted: false,
        commentCount: 0,
        inProgress: true,
        contentType: 'report',
        reportClientId: report.clientId,
        reportParentClientId: report.parentClientId,
        reportActivities: 0,
        reportWorkflowName: report.workflow?.name,
        reportStatus: report.reportStatusId,
        reportWorkflowId: report.workflow.id,
        status: 'synced',
        report: report.id,
        reportIsConfidential: report.isConfidential,
    })

    await downloadMissingUnits(logger, flowReport)

    // Update UI a new report has been downloaded
    Flow_dispatchOpenStateChange(flowReport.id, status)

    return flowReport
}

async function downloadMissingUnits(logger: ClientLogger, flowReport: FlowReport<WorkflowReference>) {
    const { activeUnitIds } = DS_getData()
    const unitIds = pipe([flowReport.subjectUnitId, ...flatten(dictValues(flowReport.flowUnitIds))], compact, uniq)
    const missingUnitIds = difference(unitIds, [...activeUnitIds])

    if (missingUnitIds.length === 0) {
        logger.info(`No missing units to download`)
        return
    }

    const { units } = await Serv_clientWithLogger(logger).getUnitsByIds(missingUnitIds)
    await Unit_writeUnits(units)
}
